/**
 * 数値または数値文字列を3桁区切りのカンマ形式にフォーマットする。
 * 整数、小数（任意の桁数を保持）、負数、文字列型数値（"12345.67" 等）、すでにカンマを含む文字列に対応。
 * 非数値文字列（地番 "1234-5" や日付 "2024-08-25" など）は元の文字列をそのまま返却する。
 * null / undefined / 空文字 の場合は fallback（デフォルト: ""）を返却する。
 *
 * @param value フォーマット対象の値（数値、文字列、null、undefined）
 * @param fallback 値が存在しない場合のフォールバック文字列（デフォルト: ""）
 * @returns 3桁カンマ区切りの文字列
 *
 * @example
 * formatNumberWithComma(1234567) // => "1,234,567"
 * formatNumberWithComma("12345.67") // => "12,345.67"
 * formatNumberWithComma(12345.6789) // => "12,345.6789"
 * formatNumberWithComma("-50000") // => "-50,000"
 * formatNumberWithComma("1234-5") // => "1234-5" (非数値はそのまま)
 * formatNumberWithComma(null, "-") // => "-"
 */
export const formatNumberWithComma = (
  value: number | string | null | undefined,
  fallback = ""
): string => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "number") {
    if (isNaN(value) || !isFinite(value)) return fallback;
    const str = String(value);
    const parts = str.split(".");
    const integerPart = Number(parts[0]).toLocaleString("ja-JP");
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  }

  // すでにカンマがついている数値文字列の場合はカンマを取り除く
  const trimmed = value.trim();
  const sanitized = trimmed.replace(/,/g, "");
  const num = Number(sanitized); // 数値ではない場合はここでNaNになる

  // 数値として解釈できない場合（"1234-5", "2024-08-25", "-" 等）は元の文字列を維持
  if (isNaN(num) || !isFinite(num) || sanitized === "") {
    return value;
  }

  // 小数点がある場合も小数を維持して3桁カンマ化
  const parts = sanitized.split(".");
  const integerPart = Number(parts[0]).toLocaleString("ja-JP");
  return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
};
